import { Component, Inject, OnInit } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "../auth.service";
import { TranslateService } from "@ngx-translate/core";
import {
  EXTENSION_AUTH_REDIRECT_URI_KEY,
  EXTENSION_AUTH_SOURCE_KEY,
  isValidExtensionRedirectUri,
} from "./extension-auth.constants";

@Component({
  selector: "app-extension-auth",
  templateUrl: "./extension-auth.component.html",
  styleUrls: ["./extension-auth.component.scss"],
  standalone: false,
})
export class ExtensionAuthComponent implements OnInit {
  redirecting = false;
  errorMessage: string | null = null;
  invalidRedirect = false;
  email = "";
  password = "";
  private redirectUri = "";
  private source = "";

  constructor(
    private readonly auth: AuthService,
    private readonly route: ActivatedRoute,
    private readonly translate: TranslateService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    const redirectUri = this.route.snapshot.queryParamMap.get("redirect_uri");
    const source = this.route.snapshot.queryParamMap.get("source") || "";
    if (!redirectUri || !isValidExtensionRedirectUri(redirectUri)) {
      this.invalidRedirect = true;
      this.errorMessage = this.translate.instant(
        "login.extensionAuthInvalidRedirect",
      ) as string;
      return;
    }
    this.redirectUri = redirectUri;
    this.source = source;
    window.sessionStorage.setItem(EXTENSION_AUTH_REDIRECT_URI_KEY, redirectUri);
    if (source) {
      window.sessionStorage.setItem(EXTENSION_AUTH_SOURCE_KEY, source);
    } else {
      window.sessionStorage.removeItem(EXTENSION_AUTH_SOURCE_KEY);
    }
  }

  get canSubmitPassword(): boolean {
    return (
      this.email.trim().includes("@") &&
      this.password.length > 0 &&
      !this.redirecting &&
      !this.invalidRedirect
    );
  }

  async loginWithGoogle(): Promise<void> {
    if (this.invalidRedirect) {
      return;
    }
    this.errorMessage = null;
    this.redirecting = true;
    try {
      await this.auth.loginWithGoogle();
    } catch {
      this.errorMessage = this.translate.instant(
        "login.extensionAuthFailed",
      ) as string;
      this.redirecting = false;
    }
  }

  async loginWithMicrosoft(): Promise<void> {
    if (this.invalidRedirect) {
      return;
    }
    this.errorMessage = null;
    this.redirecting = true;
    try {
      await this.auth.loginWithMicrosoft();
    } catch {
      this.errorMessage = this.translate.instant(
        "login.extensionAuthFailed",
      ) as string;
      this.redirecting = false;
    }
  }

  async loginWithPassword(): Promise<void> {
    if (!this.canSubmitPassword) {
      return;
    }
    this.errorMessage = null;
    this.redirecting = true;
    try {
      const issued = await this.auth.loginWithPassword(
        this.email,
        this.password,
      );
      this.finishExtensionRedirect(issued.customToken, issued.email);
    } catch {
      this.errorMessage = this.translate.instant(
        "login.extensionAuthInvalidCredentials",
      ) as string;
      this.redirecting = false;
    }
  }

  private finishExtensionRedirect(customToken: string, email: string): void {
    window.sessionStorage.removeItem(EXTENSION_AUTH_REDIRECT_URI_KEY);
    window.sessionStorage.removeItem(EXTENSION_AUTH_SOURCE_KEY);
    const target = new URL(this.redirectUri);
    target.hash = new URLSearchParams({
      custom_token: customToken,
      email,
      ...(this.source ? { source: this.source } : {}),
    }).toString();
    this.document.defaultView?.location.replace(target.toString());
  }
}
